import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { 
  TrendingUp, 
  Users, 
  DollarSign, 
  Target,
  CheckCircle2,
  Zap,
  BarChart3,
  Crown,
  Clock,
  Shield,
  Rocket,
  Star,
  Eye,
  MessageCircle,
  TrendingDown,
  ArrowRight
} from 'lucide-react';
import { AuthContext, AppSettingsContext } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BecomeTipster = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const appSettings = useContext(AppSettingsContext);
  const [stats, setStats] = useState({
    total_users: 0,
    active_bettors: 0,
    total_bets: 0
  });

  useEffect(() => {
    fetchPlatformStats();
  }, []);

  const fetchPlatformStats = async () => {
    try {
      // Fetch real platform stats (public endpoint, no auth required)
      const response = await axios.get(`${API}/public/platform-stats`);
      setStats({
        total_users: response.data.total_users || 0,
        active_bettors: response.data.active_bettors || 0, // Real active bettors (non-free users)
        total_bets: response.data.total_bets || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Ready-Made Audience',
      description: 'Access thousands of active bettors already using our platform to track their bets and improve their strategies.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: DollarSign,
      title: 'Flexible Monetization',
      description: 'Sell individual tips OR offer monthly subscriptions. You set the prices, we handle all payments securely.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: BarChart3,
      title: 'Performance Tracking',
      description: 'Automatic win rate calculations, ROI tracking, and detailed statistics that build trust with subscribers.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Target,
      title: 'Highly Engaged Users',
      description: 'Our users are serious bettors actively tracking results and seeking winning strategies - the perfect audience for tips.',
      color: 'from-orange-500 to-red-500'
    },
    {
      icon: Zap,
      title: 'Instant Notifications',
      description: 'Followers get instant notifications when you post new tips. Build a loyal audience that never misses your calls.',
      color: 'from-yellow-500 to-amber-500'
    },
    {
      icon: Shield,
      title: 'Secure & Reliable',
      description: 'We handle all payment processing, subscriber management, and platform maintenance. You focus on winning tips.',
      color: 'from-indigo-500 to-purple-500'
    }
  ];

  const monetizationOptions = [
    {
      title: 'Per-Tip Sales',
      description: 'Sell individual premium tips',
      examples: ['$5 per tip', '$10 for high-confidence picks', 'Flexible pricing per tip'],
      icon: Star
    },
    {
      title: 'Monthly Subscriptions',
      description: 'Recurring revenue from loyal followers',
      examples: ['$29.99/month unlimited tips', '$49.99/month VIP access', 'You set your price'],
      icon: Crown
    },
    {
      title: 'Hybrid Model',
      description: 'Combine both for maximum earnings',
      examples: ['Free tips to build following', 'Premium tips for sale', 'VIP subscription tier'],
      icon: Rocket
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Subscribe to Tipster Plan',
      description: 'Simple monthly subscription unlocks all tipster features',
      icon: Zap
    },
    {
      step: 2,
      title: 'Create Your Service',
      description: 'Set up your profile, branding, and pricing in minutes',
      icon: Target
    },
    {
      step: 3,
      title: 'Publish Tips',
      description: 'Share your winning picks with automatic statistics tracking',
      icon: TrendingUp
    },
    {
      step: 4,
      title: 'Earn Money',
      description: 'Add your PayPal email in settings and get paid directly from tip sales and subscriptions',
      icon: DollarSign
    }
  ];

  const benefits = [
    'No setup fees - just monthly subscription',
    'Keep majority of your earnings',
    'PayPal payment processing (add your email in profile)',
    'Automatic payment processing',
    'Built-in follower system',
    'Performance statistics tracking',
    'Professional service page',
    'Mobile-friendly interface',
    'Email notifications to followers',
    'Secure & reliable platform',
    'Active community of bettors'
  ];

  const pageContent = (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-16">
        
        {/* Hero Section */}
        <div className="text-center space-y-6 pt-8">
          <div className="inline-flex items-center justify-center p-4 bg-gradient-to-r from-blue-500/20 to-purple-500/20 rounded-2xl mb-4">
            <Crown className="w-16 h-16 text-yellow-400" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Become a <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">Tipster</span>
          </h1>
          <p className="text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Turn Your Betting Expertise Into Income
          </p>
          <p className="text-xl text-gray-400 max-w-4xl mx-auto">
            Access thousands of active bettors actively tracking their performance and seeking winning strategies. 
            This isn't just another tipster platform - it's a community of engaged users ready to discover your expertise.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-4 pt-6">
            <Button
              onClick={() => navigate(user ? '/subscription' : '/signup')}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg font-semibold shadow-xl hover:shadow-blue-500/50 transition-all"
            >
              <Rocket className="w-5 h-5 mr-2" />
              Get Started Now
            </Button>
            <Button
              onClick={() => navigate('/subscription')}
              variant="outline"
              className="border-2 border-blue-400 text-blue-400 hover:bg-blue-400/10 px-8 py-6 text-lg font-semibold"
            >
              View Pricing
            </Button>
          </div>
        </div>

        {/* Stats Section */}
        <Card className="glass-dark border-white/10 bg-gradient-to-br from-blue-900/30 to-purple-900/30">
          <CardContent className="p-8">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              Your Potential Audience
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center space-y-2">
                <Users className="w-12 h-12 text-blue-400 mx-auto" />
                <p className="text-4xl font-bold text-white">{stats.total_users.toLocaleString()}+</p>
                <p className="text-gray-300">Platform Users</p>
              </div>
              <div className="text-center space-y-2">
                <Target className="w-12 h-12 text-green-400 mx-auto" />
                <p className="text-4xl font-bold text-white">{stats.active_bettors.toLocaleString()}+</p>
                <p className="text-gray-300">Active Bettors</p>
              </div>
              <div className="text-center space-y-2">
                <BarChart3 className="w-12 h-12 text-purple-400 mx-auto" />
                <p className="text-4xl font-bold text-white">{stats.total_bets.toLocaleString()}+</p>
                <p className="text-gray-300">Bets Tracked</p>
              </div>
            </div>
            <p className="text-center text-gray-300 mt-8 text-lg">
              These aren't just numbers - they're <strong className="text-white">engaged bettors</strong> actively tracking their performance and looking for winning strategies
            </p>
          </CardContent>
        </Card>

        {/* Why This Platform is Different */}
        <div className="text-center space-y-6">
          <h2 className="text-4xl font-bold text-white">Why This Platform is Different</h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto">
            This isn't a generic tipster marketplace. It's a <strong className="text-blue-400">betting tracking platform</strong> where users come to improve their betting performance.
          </p>
          <Card className="glass-dark border-yellow-500/50 bg-gradient-to-br from-yellow-900/20 to-orange-900/20 max-w-4xl mx-auto">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <Zap className="w-8 h-8 text-yellow-400 flex-shrink-0 mt-1" />
                <div className="text-left">
                  <h3 className="text-2xl font-bold text-white mb-3">The Perfect Target Audience</h3>
                  <p className="text-gray-300 text-lg leading-relaxed">
                    Our users aren't casually browsing - they're <strong className="text-white">actively tracking bets</strong>, 
                    analyzing their performance, and <strong className="text-white">seeking strategies to improve</strong>. 
                    They're already in the mindset to invest in quality tips because they're committed to winning.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Features Grid */}
        <div>
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            Everything You Need to Succeed
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <Card key={index} className="glass-dark border-white/10 hover:border-blue-500/50 transition-all group">
                  <CardContent className="p-6">
                    <div className={`p-3 bg-gradient-to-br ${feature.color} rounded-lg w-fit mb-4 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-300 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Monetization Options */}
        <div>
          <h2 className="text-4xl font-bold text-white text-center mb-4">
            Flexible Ways to Earn
          </h2>
          <p className="text-xl text-gray-300 text-center mb-12">
            Choose the monetization model that works best for you
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            {monetizationOptions.map((option, index) => {
              const Icon = option.icon;
              return (
                <Card key={index} className="glass-dark border-white/10 bg-gradient-to-br from-green-900/20 to-emerald-900/20">
                  <CardHeader>
                    <div className="flex items-center gap-3 mb-2">
                      <Icon className="w-8 h-8 text-green-400" />
                      <CardTitle className="text-white text-2xl">{option.title}</CardTitle>
                    </div>
                    <p className="text-gray-300">{option.description}</p>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {option.examples.map((example, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                          <span className="text-gray-300 text-sm">{example}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Card className="glass-dark border-green-500/50 bg-gradient-to-br from-green-900/30 to-blue-900/30 mt-8 max-w-3xl mx-auto">
            <CardContent className="p-6 text-center">
              <DollarSign className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-xl text-white font-semibold mb-2">
                You Keep the Majority of Your Earnings
              </p>
              <p className="text-gray-300">
                Transparent fee structure with no hidden costs. We only succeed when you succeed.
              </p>
            </CardContent>
          </Card>

          {/* Payment Processing Info */}
          <Card className="glass-dark border-blue-500/50 bg-gradient-to-br from-blue-900/30 to-purple-900/30 mt-8 max-w-3xl mx-auto">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-blue-500/20 rounded-lg flex-shrink-0">
                  <DollarSign className="w-8 h-8 text-blue-400" />
                </div>
                <div className="text-left">
                  <h3 className="text-xl font-bold text-white mb-2">Get Paid via PayPal</h3>
                  <p className="text-gray-300 leading-relaxed mb-3">
                    Once you become a tipster, simply add your PayPal email address in your profile settings (Profile → User Settings). 
                    All your earnings from tip sales and subscriptions will be processed through PayPal.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm">Secure PayPal payment processing</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm">Easy to set up in your profile settings</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-300">
                      <CheckCircle2 className="w-4 h-4 text-green-400 flex-shrink-0" />
                      <span className="text-sm">Receive earnings directly to your PayPal account</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How It Works */}
        <div>
          <h2 className="text-4xl font-bold text-white text-center mb-12">
            How It Works
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {howItWorks.map((step, index) => {
              const Icon = step.icon;
              return (
                <div key={index} className="relative">
                  <Card className="glass-dark border-white/10 h-full">
                    <CardContent className="p-6 text-center">
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-xl">
                        {step.step}
                      </div>
                      <div className="pt-6">
                        <Icon className="w-10 h-10 text-blue-400 mx-auto mb-4" />
                        <h3 className="text-lg font-bold text-white mb-2">{step.title}</h3>
                        <p className="text-gray-300 text-sm">{step.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                  {index < howItWorks.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-6 h-6 text-blue-400" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Benefits List */}
        <Card className="glass-dark border-white/10">
          <CardHeader>
            <CardTitle className="text-white text-3xl text-center">
              What You Get as a Tipster
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <span className="text-gray-300">{benefit}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Final CTA */}
        <Card className="glass-dark border-white/10 bg-gradient-to-br from-blue-900/40 to-purple-900/40">
          <CardContent className="p-12 text-center">
            <h2 className="text-4xl font-bold text-white mb-4">
              Ready to Start Earning?
            </h2>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Join the platform where serious bettors discover winning strategies. 
              No hidden fees, no complicated setup - just your expertise and our engaged audience.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button
                onClick={() => navigate(user ? '/subscription' : '/signup')}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-10 py-7 text-xl font-bold shadow-2xl hover:shadow-blue-500/50 transition-all"
              >
                <Rocket className="w-6 h-6 mr-2" />
                Become a Tipster Today
              </Button>
            </div>
            <p className="text-gray-400 mt-6 text-sm">
              Questions? <button onClick={() => navigate('/advertising')} className="text-blue-400 hover:underline">Contact us</button> to learn more
            </p>
          </CardContent>
        </Card>

      </div>
    </div>
  );

  // If user is logged in, use Layout component (which includes proper header/nav)
  if (user) {
    return (
      <Layout>
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
          {pageContent}
        </div>
      </Layout>
    );
  }

  // Public view with Landing page-style navigation
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white">
      {/* Public Navigation */}
      <nav className="fixed top-0 w-full z-50 glass-dark border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              {appSettings.app_logo_url ? (
                <img 
                  src={appSettings.app_logo_url} 
                  alt={appSettings.app_name} 
                  className="h-10 max-w-[200px] object-contain"
                />
              ) : (
                <>
                  <TrendingUp className="w-8 h-8 text-blue-400" />
                  <span className="text-lg sm:text-xl font-bold whitespace-nowrap">{appSettings.app_name}</span>
                </>
              )}
            </div>
            <div className="flex items-center gap-3">
              <Button 
                onClick={() => navigate('/login')} 
                variant="ghost"
                className="text-gray-300 hover:text-white"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/signup')} 
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Content with top padding for fixed nav */}
      <div className="pt-16">
        {pageContent}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default BecomeTipster;
